/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectComponentList.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, CardContent, Grid, Paper, Typography } from "@mui/material";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import CustomSearchbarWithApiV2 from "../../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import { vlcCardStyles } from "../../../../components/customComponentsV2/vlc/config/vlcConfig";
import { componentStyle } from "../../../../config/appTheme";
import CustomPaginationV2 from "../../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import VlcCardContent from "../../../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomToolTip from "../../../../components/infoButton/CustomToolTip";
import CustomCheckBoxWithFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithFormControl";

function SelectComponentList({
  dataList,
  idKey = "id",
  selectedComponentDetails,
  renderSelectIcon = () => {},
  renderCardActionIcons = () => {},
  handleSelectAll = () => {},
  page,
  rowsPerPage,
  handlePageChange,
  totalCount,
  apiAction,
  searchKey,
  placeholder,
  getDataOnSearch,
  onClick = () => {},
  params,
  enableApiRequest = true,
  showSelectAll = false,
  enableSelectAll = false,
  setEnableSelectAll = () => {},
  isItemDisabled = () => false,
}) {
  useEffect(() => {
    setEnableSelectAll(selectedComponentDetails?.length === dataList?.length);
  }, [dataList, selectedComponentDetails, setEnableSelectAll]);
  return (
    <Grid container>
      {showSelectAll && (
        <Grid
          item
          sm={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <CustomCheckBoxWithFormControl
            label="Select All"
            value={enableSelectAll}
            disabled={dataList?.length === 0}
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleSelectAll(isChecked);
            }}
          />
        </Grid>
      )}
      <Grid item sm={12}>
        {enableApiRequest && (
          <CustomSearchbarWithApiV2
            apiAction={apiAction}
            searchKey={searchKey}
            placeholder={placeholder}
            getDataOnSearch={getDataOnSearch}
            params={params}
            iconColor={"black"}
          />
        )}
      </Grid>
      <Grid
        item
        sm={12}
        component={Paper}
        elevation={2}
        sx={{ borderRadius: 0 }}
      >
        <CardContent
          sx={{
            ...vlcCardStyles.CardContent,
            overflowY: "auto",
            p: 0,
            m: 0,
            height: showSelectAll ? "238px" : "268px",
          }}
        >
          {dataList && dataList?.length > 0 ? (
            dataList.map((item, index) => (
              <VlcCardContent
                onClick={(selectedData) => {}}
                data={item}
                key={item?.[idKey] || index}
                isSelected={selectedComponentDetails?.[idKey] === item?.id}
                cardStyle={
                  isItemDisabled(item)
                    ? { border: "none", opacity: 0.5, cursor: "pointer" }
                    : { border: "none" }
                }
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    position: "relative",
                  }}
                  onClick={() => {
                    if (!isItemDisabled(item)) {
                      onClick(item);
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: "10%",
                    }}
                  >
                    {renderSelectIcon(item.data)}
                  </Box>
                  <Box
                    sx={{
                      width: "70%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        p: 1,
                      }}
                    >
                      {item?.cardDataArray.map((cardData, cardDataIndex) => (
                        <Typography
                          key={cardData.key || cardData.id || `card-${cardDataIndex}`}
                          sx={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            display: "flex",
                            gap: 0.5,
                            ...cardData.style,
                          }}
                        >
                          <Box
                            sx={{
                              ...componentStyle.font.text,
                              ...cardData.keyStyle,
                            }}
                          >{`${cardData.key}:`}</Box>{" "}
                          <Box>
                            <CustomToolTip
                              length={30}
                              style={{
                                ...componentStyle.font.text,
                                ...cardData.valueStyle,
                              }}
                            >
                              {cardData.value}
                            </CustomToolTip>
                          </Box>
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: "20%",
                    }}
                  >
                    {renderCardActionIcons(item.data)}
                  </Box>
                </Box>
              </VlcCardContent>
            ))
          ) : (
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mt: { xs: 1, sm: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              No data available
            </Typography>
          )}
        </CardContent>
        <CustomPaginationV2
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          totalItems={totalCount}
        />
      </Grid>
      <Grid item sm={12}></Grid>
    </Grid>
  );
}

SelectComponentList.propTypes = {
  dataList: PropTypes.array,
  idKey: PropTypes.string,
  selectedComponentDetails: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  renderSelectIcon: PropTypes.func,
  renderCardActionIcons: PropTypes.func,
  handleSelectAll: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  totalCount: PropTypes.number,
  apiAction: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  searchKey: PropTypes.string,
  placeholder: PropTypes.string,
  getDataOnSearch: PropTypes.func,
  onClick: PropTypes.func,
  params: PropTypes.object,
  enableApiRequest: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  enableSelectAll: PropTypes.bool,
  setEnableSelectAll: PropTypes.func,
  isItemDisabled: PropTypes.func,
};

export default SelectComponentList;
